import { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { APP_TO_BUILD } from "../../appsConfig";
import { useRootStore } from "@/state/RootStore";
import { captureEvent } from "@/utils/posthog";
import { useIsMobileWeb } from "@/hooks/useIsMobileWeb";
import { showToastFromKey } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { localizeStateDescription } from "@/i18n/serverContentCopy";
import { buildExamOptionGroups, getSoleExamOption } from "@/utils/examSelection";
import { type ExamOption } from "@/types/examSelection";
import ExamBreakdown from "./ExamBreakdown";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { EmptyState } from "./redesign/EmptyState";
import { OnboardingHeader } from "./redesign/OnboardingHeader";
import { SearchableSelect } from "./redesign/SearchableSelect";
import { ChevronLeft } from "lucide-react";
import { shouldShowChangeExamHeader } from "@/utils/examTypeNavigation";
import { savePendingSignUpExamSelection } from "@/utils/pendingSignUpExamSelection";

interface ExamTypeSelectionProps {
  inSignUp?: boolean;
  standalone?: boolean;
}

const ExamTypeSelection = observer(({
  inSignUp = false,
  standalone = false,
}: ExamTypeSelectionProps) => {
  const store = useRootStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobileWeb = useIsMobileWeb();
  const showChangeHeader = shouldShowChangeExamHeader({ inSignUp, standalone });
  const selectedStateCode = store.mainAppStore.selectedStateCode;
  const stateInfo = store.questionsStore.stateInfo;
  const orderedCodes = useMemo(
    () => store.questionsStore.getStatesOrdered() || [],
    [stateInfo],
  );
  const groups = useMemo(
    () => buildExamOptionGroups({
      groupCombinedStateNames: APP_TO_BUILD === "electrical_us",
      orderedCodes,
      stateExamNames: stateInfo?.stateExamNames,
      stateNames: stateInfo?.stateNames,
    }),
    [orderedCodes, stateInfo],
  );
  const usesFlatExamList = APP_TO_BUILD === "plumbing";
  const flatExamOptions = useMemo(
    () => groups.flatMap((group) => group.exams),
    [groups],
  );
  const initialExam = useMemo(
    () =>
      flatExamOptions.find((exam) => exam.code === selectedStateCode) ||
      (usesFlatExamList ? flatExamOptions[0] || null : null),
    [flatExamOptions, selectedStateCode, usesFlatExamList],
  );
  const [selectedStateName, setSelectedStateName] = useState(initialExam?.stateName || "");
  const [selectedExam, setSelectedExam] = useState<ExamOption | null>(initialExam);
  const [draftCodebookYear, setDraftCodebookYear] = useState<number | null>(
    store.mainAppStore.selectedCodebookYear,
  );
  const [saving, setSaving] = useState(false);

  const selectedGroup = usesFlatExamList
    ? { exams: flatExamOptions, stateName: "" }
    : groups.find((group) => group.stateName === selectedStateName);
  const previewExam = selectedExam || selectedGroup?.exams[0] || null;
  const availableCodebookYears = store.questionsStore.getAvailableCodebookYears();
  const defaultCodebookYear = previewExam
    ? stateInfo?.codebookYear?.[previewExam.code]
    : undefined;
  const stateOptions = useMemo(() => groups.map((group) => ({
    label: group.stateName,
    searchText: [
      ...group.exams.flatMap((exam) => [exam.examName, exam.label]),
      ...group.exams.map((exam) => String(stateInfo?.codebookYear?.[exam.code] || "")),
      APP_TO_BUILD === "electrical_us" ? "NEC" : "",
    ].join(" "),
    value: group.stateName,
  })), [groups, stateInfo?.codebookYear]);

  useEffect(() => {
    if (selectedStateCode && standalone && !inSignUp) {
      navigate(isMobileWeb ? "/profile" : "/study");
    }
  }, [inSignUp, isMobileWeb, navigate, selectedStateCode, standalone]);

  useEffect(() => {
    if (!initialExam) return;
    setSelectedStateName(initialExam.stateName);
    setSelectedExam(initialExam);
  }, [initialExam]);

  useEffect(() => {
    const storedYear = store.mainAppStore.selectedCodebookYear;
    if (storedYear && selectedExam?.code === selectedStateCode) {
      setDraftCodebookYear(storedYear);
    } else {
      setDraftCodebookYear(defaultCodebookYear ?? null);
    }
  }, [defaultCodebookYear, selectedExam, selectedStateCode, store.mainAppStore.selectedCodebookYear]);

  const handleStateChange = useCallback((stateName: string) => {
    setSelectedStateName(stateName);
    setSelectedExam(getSoleExamOption(groups, stateName));
  }, [groups]);

  const handleSave = useCallback(async () => {
    if (!selectedExam) return;
    setSaving(true);
    try {
      const shouldClearCodebookYear = APP_TO_BUILD === "plumbing";
      let selectedCodebookYear: number | null = null;
      await store.mainAppStore.setSelectedStateCode(
        selectedExam.code,
        inSignUp,
        { clearCodebookYear: shouldClearCodebookYear },
      );
      if (APP_TO_BUILD !== "plumbing") {
        selectedCodebookYear = draftCodebookYear !== defaultCodebookYear
          ? draftCodebookYear
          : null;
        if (inSignUp) {
          store.mainAppStore.selectedCodebookYear = selectedCodebookYear;
        } else {
          await store.mainAppStore.setSelectedCodebookYear(selectedCodebookYear);
        }
      }
      if (inSignUp) {
        savePendingSignUpExamSelection(APP_TO_BUILD, {
          codebookYear: selectedCodebookYear,
          stateCode: selectedExam.code,
        });
      }
      captureEvent("exam_type_select", {
        exam_type: selectedExam.examName,
        exam_type_code: selectedExam.code,
      });
      if (!inSignUp) navigate(isMobileWeb ? "/profile" : "/study");
    } catch (error) {
      Sentry.captureException(error);
      showToastFromKey({
        titleKey: "errors.generic_title",
        descriptionKey: "errors.generic_body",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [
    defaultCodebookYear,
    draftCodebookYear,
    inSignUp,
    isMobileWeb,
    navigate,
    selectedExam,
    store.mainAppStore,
  ]);

  const description = previewExam
    ? stateInfo?.stateDescriptions?.[previewExam.code]
    : undefined;

  const page = (
    <main className={cn(
      "flex w-full min-w-0 flex-col overflow-hidden bg-[#f4f4f6]",
      standalone ? "h-full min-h-screen" : "h-full min-h-0",
    )}>
      <OnboardingHeader />
      {showChangeHeader && (
        <header className="flex h-14 w-full shrink-0 items-center border-b border-[#dddfe4] bg-white px-6">
          <button
            aria-label={t("common.back")}
            className="flex items-center gap-3 text-lg font-medium leading-[27px] text-[#0a0c11] outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)] focus-visible:ring-offset-2"
            onClick={() => navigate("/study")}
            type="button"
          >
            <ChevronLeft aria-hidden="true" className="h-6 w-6 text-[#5b616d]" />
            {t("examType.change_exam_type")}
          </button>
        </header>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="grid min-h-full min-w-0 grid-cols-1 items-stretch gap-6 p-4 pb-8 sm:p-6 sm:pb-10 lg:grid-cols-[360px_minmax(0,1fr)] lg:p-10 lg:pb-12">
          <section className="dp-card flex w-full flex-col gap-6 p-6 lg:h-full lg:overflow-y-auto">
            <h1 className="text-xl font-semibold leading-[26px] text-[#0a0c11]">
              {t("examType.choose_exam")}
              <span className="sr-only"> {t("examType.exam_type_heading")}</span>
            </h1>

            {!usesFlatExamList && (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold leading-5 text-[#0a0c11]">{t("examType.state_label")}</label>
                <SearchableSelect
                  ariaLabel={t("examType.state_label")}
                  emptyMessage={t("examType.no_exam_types")}
                  onValueChange={handleStateChange}
                  options={stateOptions}
                  placeholder={t("examType.select_state")}
                  searchPlaceholder={t("examType.search_placeholder")}
                  searchable={false}
                  value={selectedStateName}
                />
              </div>
            )}

            {(!selectedGroup || selectedGroup.exams.length > 1) && <fieldset className={cn("flex flex-col gap-3", !selectedGroup && "min-h-0 flex-1")}>
              <legend className="mb-3 text-sm font-semibold leading-5 text-[#0a0c11]">{t("examType.exam_type_heading")}</legend>
              {selectedGroup ? selectedGroup.exams.map((exam) => {
                const selected = selectedExam?.code === exam.code;
                return (
                  <button
                    aria-checked={selected}
                    className={cn(
                      "flex min-h-14 items-center gap-2 rounded-2xl border p-4 text-left text-base outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand)] focus-visible:ring-offset-2",
                      selected ? "border-[var(--dp-brand)] bg-[var(--dp-brand-soft)]" : "border-[#dddfe4] bg-white",
                    )}
                    key={exam.code}
                    onClick={() => setSelectedExam(exam)}
                    role="radio"
                    type="button"
                  >
                    <span className={cn("flex h-5 w-5 items-center justify-center rounded-full border", selected ? "border-[var(--dp-brand)]" : "border-[#b6bac1]") }>
                      {selected && <span className="h-2.5 w-2.5 rounded-full bg-[var(--dp-brand)]" />}
                    </span>
                    {exam.label}
                  </button>
                );
              }) : (
                <div className="min-h-0 flex-1 rounded-2xl border-[1.5px] border-dashed border-[#dddfe4]">
                  <EmptyState title={t("examType.pick_state_title")} body={t("examType.pick_state_body")} />
                </div>
              )}
            </fieldset>}

            {APP_TO_BUILD === "electrical_us" && selectedGroup && availableCodebookYears.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold leading-5">{t("examType.codebook_year_label")}</label>
                <Select value={String(draftCodebookYear || "")} onValueChange={(value) => setDraftCodebookYear(Number(value))}>
                  <SelectTrigger aria-label={t("examType.codebook_year_label")} className="h-12 rounded-lg border-[#efefef] px-4 text-base">
                    <SelectValue placeholder={t("examType.select_codebook_year")} />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {availableCodebookYears.map((year) => (
                      <SelectItem key={year} value={String(year)}>{year}{year === defaultCodebookYear ? t("examType.default_suffix") : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {description && (
              <p className="text-sm leading-[21px] tracking-[0.07px] text-[#5b616d]">
                {localizeStateDescription(description, draftCodebookYear ?? defaultCodebookYear)}
              </p>
            )}
          </section>

          <section className="dp-card flex min-h-[420px] min-w-0 flex-col p-6 lg:h-full">
            {selectedExam ? (
              <>
                <div className="min-h-0 flex-1">
                  <ExamBreakdown forceStateCode={selectedExam.code} />
                </div>
                <div className="flex justify-end pt-4">
                  <button
                    className="dp-button-primary h-14 min-w-[134px] px-8 text-lg"
                    disabled={saving}
                    onClick={handleSave}
                    type="button"
                  >
                    {saving ? t("examType.saving") : t("common.continue")}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold leading-[26px] text-[#0a0c11]">{t("examType.exam_breakdown")}</h2>
                <div className="mt-6 min-h-0 flex-1 rounded-2xl border-[1.5px] border-dashed border-[#dddfe4]">
                  <EmptyState
                    title={t("examType.preview_title")}
                    body={t("examType.preview_body")}
                  />
                </div>
                <button className="sr-only" disabled type="button">{t("common.save")}</button>
              </>
            )}
          </section>
        </div>
      </div>
    </main>
  );

  return page;
});

export default ExamTypeSelection;
